<template>
  <div class="markdown-renderer" v-html="displayedHtml"></div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, onActivated, onDeactivated } from 'vue'
import Showdown from 'showdown'

// 定义组件接收的props
const props = defineProps({
  // 需要渲染的Markdown内容
  content: {
    type: String,
    default: ''
  },
  // 打字速度（毫秒/字符）
  typingSpeed: {
    type: Number,
    default: 30
  },
  // 延迟启动打字的时间（毫秒）
  delay: {
    type: Number,
    default: 0
  },
  // 是否启用打字效果
  enableTyping: {
    type: Boolean,
    default: true
  }
})

// 定义组件发出的事件
const emit = defineEmits(['typing-complete'])

// 创建showdown转换器实例
const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
  ghCodeBlocks: true,
  // 确保有序列表正确处理，即使列表项之间有空行
  headerLevelStart: 1,
  omitExtraWLInCodeBlocks: true,
  parseImgDimensions: true,
  // 解决有序列表序号问题
  simpleLineBreaks: true,
  disableForced4SpacesIndentedSublists: true,
  // 处理有序列表的配置
  smoothLivePreview: true,
  // 确保有序列表连续编号
  literalMidWordUnderscores: true,
  // 允许列表项之间有空行
  allowEmptyLines: true
})

// 渲染后的完整HTML内容
const fullHtml = ref('')
// 当前显示的HTML内容（打字机效果）
const displayedHtml = ref('')
// 当前打字位置
const currentIndex = ref(0)
// 打字状态
const isTyping = ref(false)
// 打字定时器
let typingTimer = null
// 保存组件非激活前的打字状态
const savedTypingState = ref({
  isTyping: false,
  currentIndex: 0,
  displayedHtml: ''
})

// 启动打字机效果
const startTyping = () => {
  // 先停止之前的定时器
  stopTyping()
  
  // 如果内容为空，直接返回并触发完成事件
  if (!fullHtml.value) {
    emit('typing-complete')
    return
  }
  
  // 如果不启用打字效果，直接显示全部内容并触发完成事件
  if (!props.enableTyping) {
    displayedHtml.value = fullHtml.value
    emit('typing-complete')
    return
  }
  
  isTyping.value = true
  console.log('fullHtml.value:', fullHtml.value)
  // 启动定时器，逐字输出
  typingTimer = setInterval(() => {
    if (currentIndex.value < fullHtml.value.length) {
      // 逐字添加内容
      displayedHtml.value += fullHtml.value[currentIndex.value]
      currentIndex.value++
    } else {
      // 打字结束
      stopTyping()
      emit('typing-complete')
    }
  }, props.typingSpeed)
}

// 停止打字机效果
const stopTyping = () => {
  if (typingTimer) {
    clearInterval(typingTimer)
    typingTimer = null
  }
  isTyping.value = false
}

// 列表重排：以「纯标签（文字以：结尾）」为界，其后的列表项作为描述（去圆点、缩进2字符）。
// 描述段遇到「一级栏目标签」（核心思路/实现方式/优势/难点/方案N）或「纯标签」时结束，恢复圆点。
// 例：
//   • 初始技术矛盾：          → 保留圆点作主体
//   • 描述句1                → 去圆点、缩进（md-desc）
//   • 实现方式：              → 保留圆点作主体
//   • 具体步骤1              → 去圆点、缩进（md-desc）
//   • 与游戏模式联动：xxx     → 不是一级栏目标签，仍作为描述缩进（md-desc）
//   • 优势：xxx              → 一级栏目标签，结束缩进段，恢复圆点
const TOP_LABEL_PREFIX = /^(核心思路|实现方式|优势|难点|方案\d*)[：:]/

const restructureLabelLists = (html) => {
  if (!html) return html
  // 规则1：列表内部，以「纯标签（文字以：结尾）」为界，其后的列表项作为描述（去圆点、缩进2字符）
  html = html.replace(/<ul>([\s\S]*?)<\/ul>/g, (m, inner) => {
    const liRe = /<li>([\s\S]*?)<\/li>/g
    const items = []
    let liMatch
    while ((liMatch = liRe.exec(inner)) !== null) items.push(liMatch[0])
    if (items.length < 2) return m
    const texts = items.map(li => li.replace(/<[^>]+>/g, '').trim())
    let inDesc = false
    const rebuilt = items.map((li, i) => {
      const t = texts[i]
      if (inDesc) {
        if (/[：:]$/.test(t) || TOP_LABEL_PREFIX.test(t)) { inDesc = false; return li }
        return li.replace('<li>', '<li class="md-desc">')
      }
      if (/[：:]$/.test(t)) inDesc = true
      return li
    })
    return `<ul>${rebuilt.join('')}</ul>`
  })
  // 规则2：加粗标签段落（如 <p><strong>初始技术矛盾：</strong>内容</p>）→ 转为圆点列表项，
  // 对应第8点「核心思路：/实现方式：/优势：」的标签样式；若后面紧跟列表 → 列表项作为描述（去圆点、缩进2字符），
  // 列表内纯标签项保留圆点（作为下一级主体）
  html = html.replace(
    /<p[^>]*>((?:<strong>)[^<]{1,60}[：:][^<]*(?:<\/strong>)[\s\S]*?)<\/p>(\s*)(<ul>[\s\S]*?<\/ul>)?/g,
    (m, pInner, gap, ulBlock) => {
      const labelText = pInner.replace(/<[^>]+>/g, '').trim()
      // 方案标题（方案1：xxx）保持加粗段落，不转圆点项
      if (/^方案\d*[：:]/.test(labelText)) return m
      let out = `<ul><li>${pInner}</li>`
      if (ulBlock) {
        const liRe = /<li>([\s\S]*?)<\/li>/g
        const items = []
        let liMatch
        while ((liMatch = liRe.exec(ulBlock)) !== null) items.push(liMatch[0])
        const rebuilt = items.map(li => {
          const t = li.replace(/<[^>]+>/g, '').trim()
          // 纯标签项保留圆点，其余项作为描述去圆点缩进
          return /[：:]$/.test(t) ? li : li.replace('<li>', '<li class="md-desc">')
        })
        out += rebuilt.join('')
      }
      return out + '</ul>'
    }
  )
  return html
}

// 渲染Markdown内容并启动打字机效果
const renderAndStartTyping = () => {
  if (props.content) {
    
    fullHtml.value = restructureLabelLists(converter.makeHtml(props.content))
    console.log('fullHtml.value:', fullHtml.value)
    // 只有当enableTyping为true时才开始打字
    if (props.enableTyping) {
      // 等待指定延迟后开始打字
      if (props.delay > 0) {
        setTimeout(() => {
          startTyping()
        }, props.delay)
      } else {
        startTyping()
      }
    } else {
      // enableTyping=false：不打字，直接显示全部内容并触发完成事件
      // （startTyping 内部对 !enableTyping 有处理：显示 fullHtml 并 emit typing-complete）
      startTyping()
    }
  } else {
    fullHtml.value = ''
    displayedHtml.value = ''
  }
}

// 监听content变化，重新渲染并启动打字机效果
watch(() => props.content, () => {
  renderAndStartTyping()
})

// 监听typingSpeed变化，重启打字机效果
watch(() => props.typingSpeed, () => {
  if (isTyping.value) {
    startTyping()
  }
})

// 监听enableTyping变化，当从false变为true时开始打字
watch(() => props.enableTyping, (newValue) => {
  if (newValue && !isTyping.value && props.content) {
    renderAndStartTyping()
  }
})

// 组件挂载时渲染并启动打字机效果
onMounted(() => {
  renderAndStartTyping()
})

// 组件卸载时清理定时器
onUnmounted(() => {
  stopTyping()
})

// 组件非激活时保存状态
onDeactivated(() => {
  // 保存当前打字状态
  savedTypingState.value = {
    isTyping: isTyping.value,
    currentIndex: currentIndex.value,
    displayedHtml: displayedHtml.value
  }
  // 停止打字定时器
  stopTyping()
  console.log('组件非激活，保存打字状态:', savedTypingState.value)
})

// 组件激活时恢复状态
onActivated(() => {
  console.log('组件激活，恢复打字状态:', savedTypingState.value)
  // 恢复之前的打字状态
  if (savedTypingState.value.isTyping && fullHtml.value) {
    // 恢复显示的内容和当前位置
    displayedHtml.value = savedTypingState.value.displayedHtml
    currentIndex.value = savedTypingState.value.currentIndex
    // 继续打字
    startTyping()
  }
})
</script>

<style scoped>
.markdown-renderer {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  /* 不被外层（如 .display-text 的 pre-wrap）继承，避免 HTML 源码里的换行变成可见空白 */
  white-space: normal;
}

.markdown-renderer :deep(h1),
.markdown-renderer :deep(h2),
.markdown-renderer :deep(h3),
.markdown-renderer :deep(h4),
.markdown-renderer :deep(h5),
.markdown-renderer :deep(h6) {
  margin: 1em 0 0.5em 0;
  font-weight: 600;
  color: #2c3e50;
}

.markdown-renderer :deep(h1) {
  font-size: 24px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 0.3em;
}

.markdown-renderer :deep(h2) {
  font-size: 20px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 0.3em;
}

.markdown-renderer :deep(h3) {
  font-size: 14px;
}

.markdown-renderer :deep(h4) {
  font-size: 16px;
}

.markdown-renderer :deep(h5) {
  font-size: 14px;
}

.markdown-renderer :deep(h6) {
  font-size: 13px;
  color: #7f8c8d;
}

/* v-html 动态内容必须用 :deep 穿透，段落间距才生效 */
.markdown-renderer :deep(p) {
  margin: 1em 0;
}

.markdown-renderer :deep(ul),
.markdown-renderer :deep(ol) {
  margin: 0.5em 0 1em 0;
  padding-left: 2em;
}

.markdown-renderer :deep(li) {
  margin: 0.25em 0;
}

.markdown-renderer :deep(code) {
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  padding: 0.2em 0.4em;
  font-size: 0.9em;
  font-family: 'Courier New', Courier, monospace;
}

.markdown-renderer :deep(pre) {
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  padding: 1em;
  overflow-x: auto;
  margin: 0.5em 0 1em 0;
}

.markdown-renderer :deep(pre code) {
  background-color: transparent;
  border: none;
  padding: 0;
}

.markdown-renderer :deep(blockquote) {
  border-left: 4px solid #ddd;
  margin: 0.5em 0 1em 0;
  padding: 0.5em 1em;
  color: #666;
  background-color: #f9f9f9;
}

.markdown-renderer :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5em 0 1em 0;
}

.markdown-renderer :deep(th),
.markdown-renderer :deep(td) {
  border: 1px solid #e8e8e8;
  padding: 0.5em;
  text-align: left;
}

.markdown-renderer :deep(th) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.markdown-renderer :deep(img) {
  max-width: 100%;
  height: auto;
  margin: 0.5em 0 1em 0;
}

/* 隐藏模型内容里的 --- 分隔线（Markdown hr），v-html 动态内容需 :deep 穿透 */
.markdown-renderer :deep(hr) {
  display: none;
}

/* 「标签：」后的描述列表项：去掉圆点、相对标签文字缩进2字符（ul padding 2em + 自身 2em） */
.markdown-renderer :deep(li.md-desc) {
  list-style: none;
  padding-left: 2em;
}
</style>
